import { useParams, Link } from "react-router-dom";
import { getAnimeById, getRelatedAnime } from "@/mocks/anime";
import SEOHead from "@/components/base/SEOHead";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const anime = id ? getAnimeById(parseInt(id)) : undefined;
  const relatedAnime = anime ? getRelatedAnime(anime.id, anime.categorySlug) : [];

  if (!anime) {
    return (
      <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
        <SEOHead title="未找到 - 51动漫官网" description="该动漫资源未找到，请浏览其他内容。" />
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 mb-4">未找到该动漫资源</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const pageTitle = `${anime.title} | 51动漫在线观看与下载 | 51动漫官网`;
  const pageDesc = `${anime.title}——51动漫官网提供${anime.category}动漫${anime.title}的在线观看和下载服务。${anime.description.slice(0, 100)}`;

  return (
    <main className="pt-16 min-h-screen bg-background-50">
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${anime.title},51动漫官网,${anime.category}动漫,动漫在线观看,动漫下载,动漫全集`}
        canonicalUrl={`https://www.51anime.com/article/${anime.id}`}
        ogType="article"
      />

      <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${anime.categorySlug}`} className="hover:text-primary-500 transition-colors">{anime.category}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 font-medium">{anime.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8 mb-12">
          <div className="lg:w-[320px] flex-shrink-0">
            <div className="sticky top-24">
              <div className="relative overflow-hidden rounded-2xl aspect-[3/4] mb-5">
                <img
                  src={anime.coverImage}
                  alt={anime.title}
                  className="w-full h-full object-cover"
                  title={`${anime.title} - 51动漫在线观看与下载`}
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  {anime.isHot && (
                    <span className="bg-primary-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg">HOT</span>
                  )}
                  {anime.isNew && (
                    <span className="bg-accent-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg">NEW</span>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-500">评分</span>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <span className="text-lg font-bold text-foreground-950">{anime.rating}</span>
                  </div>
                </div>
                <div className="h-px bg-background-200"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-500">状态</span>
                  <span className="text-sm font-medium text-foreground-800">{anime.status}</span>
                </div>
                <div className="h-px bg-background-200"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-500">集数</span>
                  <span className="text-sm font-medium text-foreground-800">{anime.episodeCount}</span>
                </div>
                <div className="h-px bg-background-200"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-500">播放量</span>
                  <span className="text-sm font-medium text-foreground-800">{anime.views}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{anime.title}</h1>
            <p className="text-sm text-foreground-500 mb-6">{anime.originalTitle}</p>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {anime.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full">
                  {tag}
                </span>
              ))}
              <span className="px-3 py-1 text-xs bg-background-200 text-foreground-600 rounded-full">
                {anime.category}
              </span>
            </div>

            <section className="mb-8">
              <h2 className="text-lg font-semibold text-foreground-950 mb-3">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                {anime.description}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-lg font-semibold text-foreground-950 mb-3">制作信息</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-building-4-line text-xl text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">制作公司</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.studio}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-user-star-line text-xl text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">导演</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.director}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-calendar-line text-xl text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">开播日期</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.releaseDate}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-eye-line text-xl text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">总播放量</p>
                    <p className="text-sm font-medium text-foreground-800">{anime.views}</p>
                  </div>
                </div>
              </div>
            </section>

            <div className="flex flex-wrap gap-3">
              <a href="#" className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-play-circle-fill mr-1.5"></i>51动漫在线观看
              </a>
              <a href="#" className="px-6 py-3 bg-white hover:bg-background-100 text-foreground-800 font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap border border-background-200">
                <i className="ri-download-2-line mr-1.5"></i>51动漫下载
              </a>
              <button className="w-11 h-11 flex items-center justify-center bg-white hover:bg-background-100 rounded-full cursor-pointer border border-background-200 transition-colors">
                <i className="ri-heart-line text-foreground-600"></i>
              </button>
              <button className="w-11 h-11 flex items-center justify-center bg-white hover:bg-background-100 rounded-full cursor-pointer border border-background-200 transition-colors">
                <i className="ri-share-forward-line text-foreground-600"></i>
              </button>
            </div>
          </div>
        </div>

        {relatedAnime.length > 0 && (
          <section className="border-t border-background-200 pt-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground-950">相关推荐</h2>
              <Link
                to={`/category/${anime.categorySlug}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                更多 {anime.category} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedAnime.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-xl aspect-[3/4] mb-3">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      title={`${item.title} - 51动漫在线观看`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="flex items-center gap-1 text-white/90 text-xs">
                        <i className="ri-star-fill text-yellow-400 text-xs"></i>
                        <span>{item.rating}</span>
                        <span className="mx-1 text-white/40">·</span>
                        <span>{item.episodeCount}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}